// ─────────────────────────────────────────────────────────────────────────────
// Master Sheet Reference API — backed by:
//   GET /api/v1/reference-sheets
//   PUT /api/v1/reference-sheets/:sheetId  (multipart: referenceSheet + fileName)
// Reuses the project axios client (baseURL + auth interceptor) from src/config.js.
// NOTE: this page is only for a reference copy — do NOT point it at the live
// "sheet" import endpoint (that writes into live KPI / P&L data).
// ─────────────────────────────────────────────────────────────────────────────
import Configs, { axiosbaseurl } from "../../config"

const unwrapError = error =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  "Something went wrong."

export const getReferenceSheets = async () => {
  try {
    const res = await axiosbaseurl.get("reference-sheets")
    const body = res?.data
    // Backend convention in this project: { success, data, message }.
    // Accept both { success, data } and raw array/object payloads.
    if (body && typeof body === "object" && "success" in body) return body
    return { success: true, data: body }
  } catch (error) {
    return { success: false, message: unwrapError(error) }
  }
}

// Identify the KPI / P&L entries inside an unknown GET payload without guessing
// beyond the documented card meanings:
//   KPI Sheet – reference copy of the signed-off KPI master sheet
//   P&L Sheet – reference copy of the signed-off P&L master costing sheet.
const matchesType = (item, patterns) => {
  const haystack = [
    item?.type,
    item?.sheetType,
    item?.sheet_type,
    item?.key,
    item?.name,
    item?.sheetName,
    item?.sheet_name,
    item?.title,
    item?.category,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
  return patterns.some(p => haystack.includes(p))
}

const basenameOf = value => {
  if (!value || typeof value !== "string") return null
  const parts = value.split(/[/\\]+/)
  return parts[parts.length - 1] || null
}

const toCardMeta = item => {
  if (!item || typeof item !== "object") return null
  return {
    sheetId:
      item.sheetId ?? item.id ?? item._id ?? item.sheet_id ?? item.uuid ?? null,
    fileName:
      item.fileName ??
      item.file_name ??
      item.originalName ??
      item.original_name ??
      // Actual backend fields (per live GET response): sheetUrl holds the
      // uploaded file path, e.g. "uploads\referenceSheet-1791556191111.xlsx".
      basenameOf(item.sheetUrl) ??
      basenameOf(item.sheet_url) ??
      basenameOf(item.fileUrl) ??
      basenameOf(item.file_url) ??
      basenameOf(item.url) ??
      basenameOf(item.path) ??
      item.name ??
      null,
    fileUrl: item.sheetUrl ?? item.fileUrl ?? item.url ?? null,
    status: item.status ?? null,
    updatedAt:
      item.updatedAt ??
      item.updated_at ??
      item.uploadedAt ??
      item.uploaded_at ??
      item.importedAt ??
      item.date ??
      null,
    raw: item,
  }
}

// Returns { kpi: meta|null, costings: meta|null } where meta = toCardMeta().
export const mapReferenceSheets = payload => {
  const result = { kpi: null, costings: null }
  const data = payload?.data ?? payload
  const list = Array.isArray(data) ? data : data ? Object.values(data) : []
  // 1) Array (or object-values) of sheet records.
  for (const item of list) {
    if (!item || typeof item !== "object") continue
    if (!result.kpi && matchesType(item, ["kpi"])) result.kpi = toCardMeta(item)
    else if (
      !result.costings &&
      matchesType(item, ["p&l", "pnl", "p_l", "profit", "costing"])
    )
      result.costings = toCardMeta(item)
  }
  // 2) Keyed object fallback: { kpi: {...}, costings: {...} } / { pnl: {...} }.
  if (!result.kpi || !result.costings) {
    const root = Array.isArray(data) ? null : data
    if (root && typeof root === "object") {
      const pick = (...keys) => {
        for (const k of keys) {
          if (root[k] && typeof root[k] === "object")
            return toCardMeta({ ...root[k] })
        }
        return null
      }
      if (!result.kpi) result.kpi = pick("kpi", "kpiSheet", "kpi_sheet")
      if (!result.costings)
        result.costings = pick(
          "costings",
          "pnl",
          "pl",
          "plSheet",
          "pnlSheet",
          "profitAndLoss"
        )
    }
  }
  return result
}

export const uploadReferenceSheet = async ({ sheetId, file }) => {
  if (!sheetId) {
    return { success: false, message: "Sheet ID is missing for this upload." }
  }
  if (!file) {
    return { success: false, message: "Please select a file." }
  }
  try {
    const formData = new FormData()
    // Exact multipart field name required by the backend contract.
    formData.append("referenceSheet", file, file.name)
    // fileName location is not documented in-repo; the backend contract lists it
    // alongside the file field, so send it as a multipart field (NOT query
    // string). Do NOT set Content-Type manually — the browser/axios must
    // generate the multipart boundary automatically.
    formData.append("fileName", file.name)
    const res = await axiosbaseurl.put(
      `reference-sheets/${sheetId}`,
      formData
    )
    const body = res?.data
    if (body && typeof body === "object" && "success" in body) return body
    return { success: true, data: body, message: "Reference copy uploaded." }
  } catch (error) {
    return { success: false, message: unwrapError(error) }
  }
}

// Kept for backwards compatibility with the existing UI.
export const importMasterSheet = async ({ sheetId, type, file }) => {
  void type
  return uploadReferenceSheet({ sheetId, file })
}

// Download follows the existing project convention (see NotificationDropdown):
// the GET response stores the file at `sheetUrl` (e.g.
// "uploads\referenceSheet-1791556191111.xlsx") served under REACT_APP_IMAGE_PATH.
export const downloadReferenceSheet = async ({ fileUrl, fileName }) => {
  if (!fileUrl) {
    return { success: false, message: "No file available to download." }
  }
  try {
    const normalized = String(fileUrl).replace(/\\/g, "/")
    const base = Configs.imageUrl || ""
    const downloadUrl = `${base}${normalized}`
    const link = document.createElement("a")
    link.href = downloadUrl
    link.download = fileName || normalized.split("/").pop() || "download"
    link.target = "_blank"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    return { success: true }
  } catch (error) {
    return { success: false, message: unwrapError(error) }
  }
}

export const downloadMasterSheet = async type => {
  void type
  return { success: false, message: "Download API is not connected yet." }
}
